import NOTIFICATION_TYPES from 'src/constants/notifications';

import CoreAnalytics from './coreAnalytics';

/**
 * @typedef {object} TrackedNotificationConfig
 * @property {string} shownEvent
 * @property {string} openedEvent
 * @property {number} eventVersion
 * @property {(notification: object) => object} getProps - Type-specific payload, merged into both events.
 * @property {(outcome: object) => object} [getOpenedProps] - Picks what is sent from the click outcome.
 */

/**
 * Notification types this service reports on, and how to build each one's payload. A type absent
 * here is silently ignored by both track methods below, so call sites don't need their own guard.
 *
 * @type {Record<string, TrackedNotificationConfig>}
 */
const TRACKED_TYPES = {
  /**
   * cancellation_notification_shown - fires the first time the recipient sees this notification in
   * the current page session: a real-time toast, the bell dropdown, or an unviewed card on the
   * Notifications page.
   * cancellation_notification_opened - fires when the recipient clicks it. The caller reports
   * `navigated` (boolean) and `destination` ('my_schedule' | 'scheduling' | null).
   */
  [NOTIFICATION_TYPES.CANCELLED_APPOINTMENT]: {
    shownEvent: 'cancellation_notification_shown',
    openedEvent: 'cancellation_notification_opened',
    eventVersion: 1,
    getProps: (notification) => ({
      appointmentId: notification.context?.appointmentId,
      appointmentCount: notification.context?.appointmentCount || 1,
    }),
    getOpenedProps: ({ navigated, destination }) => ({ navigated, destination }),
  },
};

/**
 * Analytics service related to notification engagement.
 */
class NotificationAnalytics extends CoreAnalytics {
  // The bell dropdown and the notifications page each re-render the same notification on every
  // open, so without this a single delivery would inflate the "shown" denominator every time the
  // recipient reopens the bell. Tracked per page session only - a fresh load can report it again.
  shownNotificationIds = new Set();

  /**
   * Track that a tracked notification type became visible to its recipient (toast or bell).
   *
   * @param {{id: string, type: string, context?: object}} notification
   * @param {string} entryPoint
   */
  trackNotificationShown(notification, entryPoint) {
    const config = TRACKED_TYPES[notification?.type];

    if (!config || !notification.id || this.shownNotificationIds.has(notification.id)) {
      return;
    }

    this.shownNotificationIds.add(notification.id);

    this.track(config.shownEvent, {
      entryPoint,
      notificationId: notification.id,
      ...config.getProps(notification),
      eventVersion: config.eventVersion,
    });
  }

  /**
   * Track that a tracked notification type was clicked/opened by its recipient.
   *
   * @param {{id: string, type: string, context?: object}} notification
   * @param {string} entryPoint
   * @param {object} [outcome] - What happened on the click; the type picks the fields it reports.
   */
  trackNotificationOpened(notification, entryPoint, outcome = {}) {
    const config = TRACKED_TYPES[notification?.type];

    if (!config || !notification.id) {
      return;
    }

    this.track(config.openedEvent, {
      entryPoint,
      notificationId: notification.id,
      ...config.getProps(notification),
      ...config.getOpenedProps?.(outcome),
      eventVersion: config.eventVersion,
    });
  }
}

export default new NotificationAnalytics();
